import * as path from "path";
import { findProjectRoot, shouldTagElement } from "../utils";

const validExtensions = new Set([".jsx", ".tsx"]);
const projectRoot = findProjectRoot();
const tailwindInputFile = path.resolve(projectRoot, "./tailwind.config.ts");
const tailwindJsonOutfile = path.resolve(
  projectRoot,
  "./src/tailwind.config.kulp.json"
);
const tailwindIntermediateFile = path.resolve(
  projectRoot,
  "./.kulp.tailwind.config.js"
);
const isSandbox = process.env.KULP_DEV_SERVER === "true";

interface ComponentTaggerStats {
  totalFiles: number;
  processedFiles: number;
  totalElements: number;
}

interface ComponentTaggerOptions {
  enableTailwindGeneration?: boolean;
  tailwindConfigPath?: string;
  tailwindOutputPath?: string;
}

export function createComponentTaggerPlugin(options: ComponentTaggerOptions = {}) {
  const cwd = process.cwd();
  const stats: ComponentTaggerStats = {
    totalFiles: 0,
    processedFiles: 0,
    totalElements: 0
  };

  return {
    name: "vite-plugin-component-tagger",
    enforce: "pre" as const,
    async transform(code: string, id: string) {
      if (!validExtensions.has(path.extname(id)) || id.includes("node_modules")) {
        return null;
      }

      stats.totalFiles++;
      const relativePath = path.relative(cwd, id);
      


      try {
        // Dynamic imports for optional dependencies
        let parse: any, MagicString: any, walk: any;
        
        try {
          const babelParser = await import("@babel/parser");
          parse = babelParser.parse;
        } catch (error) {
          console.error('[ComponentTagger] @babel/parser is required for component tagging. Please install it: npm install @babel/parser');
          return null;
        }

        try {
          const magicStringModule = await import("magic-string");
          MagicString = magicStringModule.default;
        } catch (error) {
          console.error('[ComponentTagger] magic-string is required for component tagging. Please install it: npm install magic-string');
          return null;
        }

        try {
          const estreeWalker = await import("estree-walker");
          walk = estreeWalker.walk;
        } catch (error) {
          console.error('[ComponentTagger] estree-walker is required for component tagging. Please install it: npm install estree-walker');
          return null;
        }

        const parserOptions: any = {
          sourceType: "module",
          plugins: ["jsx", "typescript"]
        };

        const ast = parse(code, parserOptions);
        const magicString = new MagicString(code);
        let changedElementsCount = 0;
        let currentElement: any = null;

        walk(ast as any, {
          enter(node: any) {
            if (node.type === "JSXElement") {
              currentElement = node;
            }

            if (node.type === "JSXOpeningElement") {
              const jsxNode = node;
              let elementName: string;

              if (jsxNode.name.type === "JSXIdentifier") {
                elementName = jsxNode.name.name;
              } else if (jsxNode.name.type === "JSXMemberExpression") {
                const memberExpr = jsxNode.name;
                elementName = `${memberExpr.object.name}.${memberExpr.property.name}`;
              } else {
                return;
              }

              if (elementName === "Fragment" || elementName === "React.Fragment") {
                return;
              }

              const attributes = jsxNode.attributes.reduce((acc: Record<string, string>, attr: any) => {
                if (attr.type === "JSXAttribute") {
                  if (attr.value?.type === "StringLiteral") {
                    acc[attr.name.name] = attr.value.value;
                  } else if (attr.value?.type === "JSXExpressionContainer" && attr.value.expression.type === "StringLiteral") {
                    acc[attr.name.name] = attr.value.expression.value;
                  }
                }
                return acc;
              }, {});

              let textContent = "";
              if (currentElement && currentElement.children) {
                textContent = currentElement.children.map((child: any) => {
                  if (child.type === "JSXText") {
                    return child.value.trim();
                  } else if (child.type === "JSXExpressionContainer") {
                    if (child.expression.type === "StringLiteral") {
                      return child.expression.value;
                    }
                  }
                  return "";
                }).filter(Boolean).join(" ").trim();
              }

              const content: Record<string, string> = {};
              if (textContent) {
                content.text = textContent;
              }
              if (attributes.placeholder) {
                content.placeholder = attributes.placeholder;
              }
              if (attributes.className) {
                content.className = attributes.className;
              }

              const line = jsxNode.loc?.start?.line ?? 0;
              const col = jsxNode.loc?.start?.column ?? 0;
              const dataComponentId = `${relativePath}:${line}:${col}`;
              const fileName = path.basename(id);
              const shouldTag = shouldTagElement(elementName);

              if (shouldTag) {

                
                const legacyIds = ` data-component-path="${relativePath}" data-component-line="${line}" data-component-file="${fileName}" data-component-name="${elementName}" data-component-content="${encodeURIComponent(
                  JSON.stringify(content)
                )}"`;

                magicString.appendLeft(
                  jsxNode.name.end ?? 0,
                  ` data-kulp-id="${dataComponentId}" data-kulp-name="${elementName}" ${legacyIds}`
                );
                changedElementsCount++;
              }
            }
          }
        });

        stats.processedFiles++;
        stats.totalElements += changedElementsCount;



        return {
          code: magicString.toString(),
          map: magicString.generateMap({ hires: true })
        };
      } catch (error) {
        console.error(`Error processing file ${relativePath}:`, error);
        stats.processedFiles++;
        return null;
      }
    },

    async buildStart() {
      if (!isSandbox) return;

      try {
        await generateConfig();
      } catch (error) {
        console.error("Error generating tailwind.config.kulp.json:", error);
      }
    },

    configureServer(server: any) {
      if (!isSandbox) return;

      try {
        server.watcher.add(tailwindInputFile);
        server.watcher.on("change", async (changedPath: string) => {
          if (path.normalize(changedPath) === path.normalize(tailwindInputFile)) {
            await generateConfig();
          }
        });
      } catch (error) {
        console.error("Error adding watcher:", error);
      }
    }
  };
}

async function generateConfig() {
  try {
    const esbuild = await import("esbuild");
    const fs = await import("fs/promises");
    
    await esbuild.build({
      entryPoints: [tailwindInputFile],
      outfile: tailwindIntermediateFile,
      bundle: true,
      format: "esm",
      banner: {
        js: 'import { createRequire } from "module"; const require = createRequire(import.meta.url);'
      }
    });

    try {
      const userConfig = await import(
        tailwindIntermediateFile + "?update=" + Date.now()
      );

      if (!userConfig || !userConfig.default) {
        console.error("Invalid Tailwind config structure:", userConfig);
        throw new Error("Invalid Tailwind config structure");
      }

      const resolveConfig = (await import("tailwindcss/resolveConfig.js")).default;
      const resolvedConfig = resolveConfig(userConfig.default);
      await fs.writeFile(
        tailwindJsonOutfile,
        JSON.stringify(resolvedConfig, null, 2)
      );

      await fs.unlink(tailwindIntermediateFile).catch(() => {
        // Ignore errors when unlinking intermediate file
      });
    } catch (error) {
      console.error("Error processing config:", error);
      throw error;
    }
  } catch (error) {
    console.error("Error in generateConfig:", error);
    throw error;
  }
}

// Legacy export for backward compatibility
export function createComponentTagger(options?: ComponentTaggerOptions) {
  return createComponentTaggerPlugin(options);
}

// Export utility functions for advanced usage
export { shouldTagElement, findProjectRoot }; 